import { useClient } from '@/contexts/Services'
import { auth } from '@/lib/firebase'
import type { RoleCategoryKey } from '@/services/masterData/roleCategories'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

// GET /roles/categories is @RequireBranch, and the axios interceptor only
// attaches branch-id once Auth's ensureValidCurrentBranch has written
// localStorage.currentBranch — which it clears first on every app load. This
// query is gated only on Firebase auth (it can't use useAuth(): AuthProvider
// itself calls useRoleCategory), so without waiting it races branch
// resolution and fires a guaranteed 400 "branch-id header is required".
// After the timeout we fire anyway so a zero-branch account fails loudly
// instead of hanging.
const waitForCurrentBranch = async (timeoutMs = 8000): Promise<void> => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeoutMs) {
    const branch = localStorage.getItem('currentBranch')
    if (branch && branch !== 'undefined' && branch !== 'null' && !isNaN(Number(branch))) {
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
}

export function useRoleCategory(category: RoleCategoryKey) {
  const client = useClient()

  const { data: categories, isLoading } = useQuery({
    queryKey: ['master-data', 'role-categories'],
    queryFn: async () => {
      await waitForCurrentBranch()
      return client!.masterData.getRoleCategories()
    },
    staleTime: Infinity,
    // Gate on tenant: fires once a tenant is set.
    enabled: !!client && !!auth.currentUser && !!localStorage.getItem('tenantId'),
  })

  const roleIdList = useMemo(() => categories?.[category] ?? [], [categories, category])
  const roleIds = useMemo(() => roleIdList.join(','), [roleIdList])

  return { roleIds, roleIdList, isLoading }
}
